import { isNumber } from 'lodash-es'
import numeral from 'numeral'
import CurrencySymbols from '@/lib/CurrencySymbolConstants.js'

export const SCALING_FACTORS = {
  K: 1000,
  M: 1000000,
  B: 1000000000,
  T: 1000000000000
}

/**
 * Returns localised string with amount in given currency
 * @param amount
 * @param currency
 * @returns {string}
 */
// Cache Intl.NumberFormat instances by (locale|currency|maxFD|notation)
const numberFormatCache = new Map()

/**
 * Normalizes negative zero and tiny values that would round to zero at the
 * current display precision, preventing UI output like `-$0`.
 */
const normalizeDisplayAmount = (amount, maximumFractionDigits = 0) => {
  if (!Number.isFinite(amount)) return amount
  if (Object.is(amount, -0)) return 0

  const zeroRoundingThreshold = 0.5 / 10 ** maximumFractionDigits
  return Math.abs(amount) < zeroRoundingThreshold ? 0 : amount
}

export const getCachedNumberFormat = (
  locale,
  currency = 'USD',
  maximumFractionDigits = 0,
  notation = 'standard',
  includeCurrencySymbol = true
) => {
  const key = `${locale}|${currency}|${maximumFractionDigits}|${notation}|${includeCurrencySymbol}`
  let nf = numberFormatCache.get(key)
  if (!nf) {
    nf = new Intl.NumberFormat(locale, {
      style: includeCurrencySymbol ? 'currency' : 'decimal',
      maximumFractionDigits,
      currency: includeCurrencySymbol ? currency : undefined,
      notation
    })
    numberFormatCache.set(key, nf)
  }
  return nf
}

export const localiseCurrency = (
  amount,
  currency = 'USD',
  maximumFractionDigits = 0,
  notation = 'standard',
  includeCurrencySymbol = true
) => {
  const locale = navigator.language
  return getCachedNumberFormat(
    locale,
    currency,
    maximumFractionDigits,
    notation,
    includeCurrencySymbol
  ).format(normalizeDisplayAmount(amount, maximumFractionDigits))
}

export const createNumberFormat = ({
  currency = 'USD',
  maximumFractionDigits = 0,
  includeCurrencySymbol = true
} = {}) => {
  const locale = navigator.language
  return getCachedNumberFormat(
    locale,
    currency,
    maximumFractionDigits,
    'standard',
    includeCurrencySymbol
  )
}

export const localiseCurrencyOptions = (amount, numberFormat) => {
  const maximumFractionDigits = numberFormat?.resolvedOptions?.().maximumFractionDigits ?? 0
  return numberFormat.format(normalizeDisplayAmount(amount, maximumFractionDigits))
}

//TODO: potentially unsafe when decimal separator is not a dot/comma
const NON_DIGITS_RE = /[^0-9.-]+/g

export const extractNumber = (stringValue) => {
  if (isNumber(stringValue)) return stringValue

  return stringValue?.length ? Number(stringValue.replace(NON_DIGITS_RE, '')) : 0
}

/**
 * Formats a number by rounding it off and using abbreviations like "M" and "K".
 * @param {number} number - The number to be formatted.
 * @returns {string} The formatted number string.
 */
export const shortenNumber = (number) => {
  return numeral(number).format('0.0 a')
}

/**
 * Abbreviates a numerical value with a unit and returns the formatted value and its unit value.
 *
 * @param {number} inputValue - The numerical value to be abbreviated.
 * @returns {Object} - {formattedValue, unitValue} An object containing the formatted value and its corresponding unit value.
 */
export const abbreviateWithUnitValue = (inputValue) => {
  const formatted = numeral(inputValue).format('0.0a').toUpperCase()
  const scalingFactor = formatted.charAt(formatted.length - 1)
  if (!Object.keys(SCALING_FACTORS).includes(scalingFactor)) {
    let value = 1
    if (inputValue >= 100) {
      value = 100
    } else if (inputValue >= 10) {
      value = 10
    }
    return {
      formattedValue: formatted,
      unitValue: value
    }
  }

  return {
    formattedValue: formatted,
    unitValue: SCALING_FACTORS[scalingFactor]
  }
}

export const abbreviateWithUnitValueCustomFormat = ({ value, format = '0.0a' }) => {
  const formatted = numeral(value).format(format).toUpperCase()
  const scalingFactor = formatted.charAt(formatted.length - 1)
  if (!Object.keys(SCALING_FACTORS).includes(scalingFactor)) {
    let result = 1
    if (value >= 100) {
      result = 100
    } else if (value >= 10) {
      result = 10
    }

    return {
      formattedValue: formatted,
      unitValue: result
    }
  }

  return {
    formattedValue: formatted,
    unitValue: SCALING_FACTORS[scalingFactor]
  }
}

// basic number formating
export const numberFormat = (value, format = '0,0') => numeral(value).format(format)

/**
 * Function to find currency object from static list of currencies
 * @param str is the name or code of the currency you want to find, @param key defines first parameter (str)
 * @returns found currency object
 */
export const getCurrencyData = (str, key) =>
  CurrencySymbols.find((currency) => currency[key] === str) || {}

// Function to convert monetary string to actual number
export const convertMonetaryStringToNumber = (str) => {
  // Remove the last character and check if it is 'm' or 'k'
  const value = str.slice(0, -1)
  const suffix = str.slice(-1)
  let number = numeral(str).value()

  if (SCALING_FACTORS[suffix.toUpperCase()]) {
    number = numeral(value).value() * SCALING_FACTORS[suffix.toUpperCase()]
  }

  return number
}

/**
 * Formats currency data for display, including abbreviation and symbol, with an option to show zero values.
 *
 * @param {number} val - The currency value to be formatted.
 * @param {boolean} showZero - Flag to determine if zero values should be displayed.
 * @returns {string} The formatted currency data.
 */
export const formatCurrencyData = (currencyData, val, showZero = false) => {
  let result = ''
  if (val !== 0) {
    result = `${currencyData.code ?? 'USD'} ${abbreviateWithUnitValue(val).formattedValue}`
  } else if (showZero) {
    result = `${currencyData.code ?? 'USD'} 0`
  }
  return result
}
